import React, { memo, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile, tablet } from 'services/ui';
import { Colors, NetworkErrors } from 'context';
import {
  appState,
  isCareerViewer,
  useCareerProfile,
  useGrowth,
  useMyCareerCenter,
  useProfessions,
  useShare,
  useSkillMap,
} from 'core';
import { authState } from 'core/state';
import { PATHS } from 'router/config';

import { CareerPreviewNotice } from 'components/pages/CareerCenter';

import { GrowthCareerInvite } from './parts/GrowthCareerInvite';
import { GrowthFlow } from './parts/GrowthFlow';
import { GrowthLevelBar } from './parts/GrowthLevelBar';
import { GrowthPortrait } from './parts/GrowthPortrait';
import { GrowthPromoCodes } from './parts/GrowthPromoCodes';
import { GrowthShare } from './parts/GrowthShare';
import { GrowthTracks } from './parts/GrowthTracks';

/**
 * /growth — «Мой рост»: очки, медали и карьерные треки (ТЗ 0039, шаг 4).
 *
 * 🔴 Раздел ВЕРХНЕГО уровня и БЕЗ гейта членства в карьерном центре. Это не
 * оплошность, а суть: экран продающий, и тот, кто ещё учится, — самый готовый
 * покупатель следующего курса. Внутри `/career` он был бы невидим ровно той
 * аудитории, ради которой делается: не-участник получает там 404, а пункт меню
 * ему вообще не показывают.
 *
 * Медали, недостижимые вне центра, приходят с `locked` и показываются с
 * замком и подписью «доступно участникам» — это и есть апселл, поэтому
 * прятать их нельзя.
 *
 * ТЗ 0039, доводка UX: страница стала ХАБОМ. Владелец сказал прямо — «сделано
 * очень просто, непонятно, как этим пользоваться» и «нету именно связи между
 * ними». Вкладки ?tab= разъехались по настоящим адресам (/growth/skills,
 * /growth/professions, /growth/achievements), а здесь остались портрет,
 * уровень и СХЕМА связей курс → навыки → профессия → вакансии.
 */
export const Growth = memo(() => {
  const { t, i18n } = useTranslation();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const { growth, loading, chooseTrack, choosing } = useGrowth();
  // Портрет и схема связей: анкета даёт «кто я», карта навыков и профессии —
  // настоящие числа для цепочки. Числа обязаны быть настоящими: схема с
  // выдуманными расходится с подстраницами, и ей перестают верить целиком.
  const { profile } = useCareerProfile();
  const { skillMap } = useSkillMap();
  const { professions } = useProfessions();
  // Методист смотрит раздел глазами студента (ТЗ 0039 доводка). Раньше он
  // открывался ему БЕЗ гарда и без данных: пачка молчаливых PermissionDenied
  // в консоль и полупустой экран.
  const { center, isPreview, loading: centerLoading } = useMyCareerCenter();

  // 🔴 Гард по роли, а не только по членству. Раздел открыт студенту И
  // методисту-смотрящему; преподаватель по прямому адресу получал не 404, а
  // полупустой экран с молчаливыми отказами в консоли.
  const currentState = appState();
  useEffect(() => {
    if (!isCareerViewer(active_role) || isDemoUser) {
      appState({ ...currentState, networkError: NetworkErrors.pageIsNotAvailable });
    }
  }, [active_role, isDemoUser, currentState]);
  // Адрес школы приходит с сервера вместе с вариантами репоста: одно место,
  // где он задаётся, — настройка бэкенда. Язык передаём ТОТ ЖЕ, что и
  // `GrowthShare` внутри: с разными переменными Apollo считает это разными
  // запросами и на каждый заход уходило два `MyShareOptions` вместо одного.
  const { options: shareOptions } = useShare(i18n.language === 'ru' ? 'RUS' : 'POL');
  const siteUrl = shareOptions[0]?.targetUrl ?? '';

  const earned = useMemo(
    () => (growth?.achievements ?? []).filter((row) => row.earned).length,
    [growth],
  );

  // Ближайшая профессия — по ДОЛЕ закрытых навыков, а не по их числу: иначе
  // «12 из 40» обгоняет «8 из 9», и схема ведёт человека дальше от цели.
  const bestProfession = useMemo(() => {
    const ranked = [...professions]
      .filter((row) => row.totalCount > 0)
      .sort((a, b) => b.haveCount / b.totalCount - a.haveCount / a.totalCount);
    return ranked[0] ?? null;
  }, [professions]);

  // Ждём и карту членства: `isPreview` приходит ДРУГИМ запросом, и на первом
  // кадре он был бы false — то есть смотрящему успевали мигнуть блок
  // промокодов, кликабельные треки и продающий блок «вступите в центр».
  if ((loading && !growth) || centerLoading) {
    return (
      <Center>
        <Spin size="large" />
      </Center>
    );
  }

  if (!growth) {
    return (
      <PageWrapper>
        {/* Плашка и здесь: именно эта ветка и есть «полупустой экран без
            объяснения», против которого режим предпросмотра сделан. */}
        {isPreview ? <CareerPreviewNotice /> : null}
        <Title>{t('growth.title')}</Title>
        <Muted>{t('growth.empty')}</Muted>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {isPreview ? <CareerPreviewNotice /> : null}
      <Header>
        <div>
          <Title>{t('growth.title')}</Title>
          <Subtitle>{t('growth.subtitle')}</Subtitle>
        </div>
        {growth.isCareerMember ? (
          <CenterLink to={PATHS.careerCenter}>{t('growth.toCareerCenter')}</CenterLink>
        ) : null}
        <Counter>
          {/* Знаменатель — ДОСТУПНЫЕ студенту медали, а не все существующие:
              «0 из 13» у того, кто не в карьерном центре, считает и те, что
              он получить не может. */}
          {t('growth.medalsCount', { n: earned, total: growth.availableTotal })}
        </Counter>
      </Header>

      {profile ? (
        <GrowthPortrait
          firstName={profile.values.firstName}
          lastName={profile.values.lastName}
          photoUrl={profile.photoUrl}
          desiredPosition={profile.values.desiredPosition}
          seniorityLabel={
            profile.values.seniority
              ? t(`careerProfile.seniority.${profile.values.seniority.toLowerCase()}`, {
                  defaultValue: profile.values.seniority,
                })
              : ''
          }
          directionTitle={center?.directionTitle ?? null}
          completeness={profile.completeness}
          skillsCount={profile.values.skills.length}
        />
      ) : null}

      <LevelRow>
        <LevelBarBox>
          <GrowthLevelBar growth={growth} />
        </LevelBarBox>
        <MedalsLink to={PATHS.growthAchievements}>{t('growth.medals.link')}</MedalsLink>
      </LevelRow>

      <GrowthFlow
        skillsMine={skillMap?.mine.length ?? 0}
        skillsMissing={skillMap?.missing.length ?? 0}
        bestProfession={bestProfession}
        professionsTotal={professions.length}
        liveVacancies={bestProfession?.liveVacancies ?? 0}
      />

      {/* Приглашение вступить — студенту, а не сотруднику: методисту
          вступать некуда, и продающий блок читался бы как ошибка. */}
      {!growth.isCareerMember && !isPreview && <GrowthCareerInvite siteUrl={siteUrl} />}

      <GrowthTracks
        tracks={growth.tracks}
        onChoose={chooseTrack}
        choosing={choosing}
        readOnly={isPreview}
      />

      <GrowthShare readOnly={isPreview} />

      {isPreview ? null : <GrowthPromoCodes siteUrl={siteUrl} />}
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  /* 🔴 width: 100% обязателен (бэктиков в этом комментарии быть не может —
     он внутри шаблонной строки): шаблон страницы это flex-СТРОКА, и без него
     обёртка сжимается по содержимому. На десктопе это давало страницу в
     половину ширины контейнера и одну колонку карточек вместо трёх. Ни tsc,
     ни сборка, ни тесты этого не видят — только глаза. */
  width: 100%;
  padding: 40px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 28px;

  ${tablet} {
    padding: 28px 0 60px;
  }

  ${mobile} {
    padding: 20px 0 48px;
  }
`;

const LevelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const LevelBarBox = styled.div`
  flex: 1 1 320px;
  min-width: 0;
`;

const MedalsLink = styled(Link)`
  flex: 0 0 auto;
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.black};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  ${fontService.getFont({ size: 32, lineHeight: 40, weight: FontWeight.bold })}
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  max-width: 640px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const CenterLink = styled(Link)`
  /* Подчёркнутая ссылка обычным цветом: жёлтый текст на белом — 1.33:1. */
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Counter = styled.span`
  padding: 6px 14px;
  border-radius: 20px;
  background: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Muted = styled.p`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;
